import type { SelectChangeEvent } from "@mui/material";
import { CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import type { Option } from "../constants";
import { canvasSelectSx } from "../forms/styled/CanvasFormTextField.styled";

export interface FormulaOperatorsProps {
	onSelect: (value: string) => void;
	options?: Option[];
	/** When true, never loads from getFormulaOperators; use `options` (may be empty while parent loads). */
	suppressAutoFetch?: boolean;
	fullWidth?: boolean;
	disabled?: boolean;
	label?: string;
}

/**
 * Insert-style operator picker for formula building.
 * The visible value always resets to empty after selection so the same
 * operator can be inserted multiple times; callers should use `onSelect`
 * to append the chosen token into the formula text area.
 */
const FormulaOperators = (props: FormulaOperatorsProps) => {
	const {
		onSelect,
		options,
		suppressAutoFetch = false,
		fullWidth = true,
		disabled = false,
		label = "Operator",
	} = props;

	const authHeader = useAuthHeader();
	const [fetchedOperators, setFetchedOperators] = useState<Option[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (suppressAutoFetch) return;
		if (options?.length) return;

		let cancelled = false;
		const fetchOperators = async () => {
			setLoading(true);
			try {
				const response = await axios.get(apiRoutes.getFormulaOperators, { headers: authHeader });
				if (!cancelled) {
					const mapped: Option[] = (response.data ?? []).map((op: { id: string; operator: string; label?: string }) => ({
						label: op.label ?? op.operator,
						value: op.operator,
					}));
					setFetchedOperators(mapped);
				}
			} catch {
				if (!cancelled) setFetchedOperators([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchOperators();

		return () => { cancelled = true; };
	}, [authHeader, options, suppressAutoFetch]);

	const operators = suppressAutoFetch ? (options ?? []) : (options?.length ? options : fetchedOperators);

	return (
		<FormControl fullWidth={fullWidth} disabled={disabled} sx={canvasSelectSx}>
			<InputLabel>{label}</InputLabel>
			<Select
				value=""
				label={label}
				onChange={(event: SelectChangeEvent) => { if (event.target.value) onSelect(event.target.value); }}
				endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : undefined}
			>
				<MenuItem value="" disabled>
					Add Operator
				</MenuItem>
				{operators.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

export default FormulaOperators;
